import { useTranslation } from "react-i18next";
import { getAllPoliJobsCvAccessReport } from "../../Requests/report-requests";
import { getPoliJobsCvAccessReportInfo } from "../../utils/reactQueriesConstants";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import {
	PoliJobsCvAccessReportDTO,
	PoliJobsReportSortFieldEnum,
	Role,
} from "../../Api";
import {
	Button,
	Dropdown,
	Menu,
	MenuProps,
	Pagination,
	Progress,
	Table,
	Typography,
} from "antd";
import { useCallback, useState } from "react";
import { useQuery } from "react-query";
import {
	NOTIFICATION_TYPES,
	openNotification,
} from "../Notifications/NotificationsUtils";
import useQueryFilters from "../../Hooks/useQueryFilters";
import Filters from "../Filters";
import { ItemType } from "antd/lib/menu/hooks/useItems";
import { ActionKeyEnum } from "./PoliJobsReport.types";
import { downloadExcel, downloadFile } from "../../utils/downloadUtils";
import { generateStudentAccessQRCodePdf } from "../../Requests/polijobs-market-requests";
import { removeDiacritics } from "../../utils/dataUtils";

const PoliJobsReport = () => {
	const { t } = useTranslation();
	const query = useQueryFilters({});
	const [pageSize, setPageSize] = useState(10);
	const [downloadError, setDownloadError] = useState(false);
	const [downloading, setDownloading] = useState(false);
	const [progress, setProgress] = useState(0);

	const downloadFail = (_err: any) => {
		openNotification(
			t("polijobs.error"),
			t("polijobs.downloadError"),
			NOTIFICATION_TYPES.ERROR
		);
	};

	const getActions = (
		record: PoliJobsCvAccessReportDTO
	): ItemType[] | undefined => {
		let actions: MenuProps["items"] = [];

		actions.push({
			label: t("polijobs.downloadQRCode"),
			key: ActionKeyEnum.GenerateQRCode,
			icon: (
				<FontAwesomeIcon
					icon={solid("download")}
					style={{ fontSize: "15px" }}
				/>
			),
			onClick: () =>
				downloadFile(
					generateStudentAccessQRCodePdf(record.id!),
					t("documents.QR")
				).catch(downloadFail),
		});

		actions.push({
			label: t("polijobs.copyCvShareLink"),
			key: ActionKeyEnum.CopyCVSharingLink,
			icon: (
				<FontAwesomeIcon
					icon={solid("clipboard")}
					style={{ fontSize: "15px" }}
				/>
			),
			onClick: () => {
				navigator.clipboard
					.writeText(
						`https://${process.env.REACT_APP_DOMAIN}/partajare-cv/${record.id}`
					)
					.then(() => {
						openNotification(
							t("polijobs.copyCvShareLink"),
							t("polijobs.cvShareLinkCopiedMessage"),
							NOTIFICATION_TYPES.SUCCESS
						);
					});
			},
		});

		return actions;
	};

	const columns = [
		{
			title: t("reports.companyName"),
			key: "companyName",
			dataIndex: "companyName",
			render: (
				_text: string | undefined,
				record: PoliJobsCvAccessReportDTO
			) => (
				<Link
					to={{
						pathname: `/profil`,
						state: {
							id: record.id,
							userType: Role.Company,
						},
					}}
				>
					<b>{record.companyName}</b>
				</Link>
			),
		},
		{
			title: t("companies.cui"),
			dataIndex: "companyCui",
			key: "companyCui",
		},
		{
			title: t("reports.cvSharesCount"),
			key: "cvSharesCount",
			dataIndex: "cvSharesCount",
		},
		{
			title: t("reports.cvDownloadsCount"),
			key: "cvDownloadsCount",
			dataIndex: "cvDownloadsCount",
		},
		{
			title: t("companies.actions"),
			key: "actions",
			fixed: "right" as "right",
			width: 90,
			render: (_text: string | undefined, record: any) => (
				<div>
					<Dropdown
						placement="bottomLeft"
						overlay={<Menu items={getActions(record)} />}
					>
						<FontAwesomeIcon
							icon={solid("bars")}
							style={{
								cursor: "pointer",
								height: "1rem",
								padding: "6px 0px",
								marginLeft: "13px",
							}}
						/>
					</Dropdown>
				</div>
			),
		},
		{
			title: t("reports.cvsList"),
			key: "students",
			fixed: "right" as "right",
			width: 85,
			render: (
				_text: string | undefined,
				record: PoliJobsCvAccessReportDTO
			) => (
				<Link
					to={{
						pathname: `/cv-studenti/${record.id}`,
					}}
				>
					<FontAwesomeIcon
						icon={solid("arrow-right")}
						title={t("reports.viewCVsList")}
					/>
				</Link>
			),
		},
	];

	const excelColumns = columns.filter(
		(column) => column.key !== "actions" && column.key !== "students"
	);

	const openGetErrorNotification = (_error: any) => {
		openNotification(
			t("usersText.errorTexts.failedUserGet"),
			t("usersText.errorTexts.serverFailedDescription"),
			NOTIFICATION_TYPES.ERROR
		);
	};

	const { data, isLoading: loading } = useQuery(
		[getPoliJobsCvAccessReportInfo, query.filters],
		() => getAllPoliJobsCvAccessReport(query.filters),
		{
			onError: openGetErrorNotification,
		}
	);

	const onUpdate = useCallback(
		(values: { [key: string]: string[] }) => {
			query.update(values, true);
		},
		[query]
	);

	const onPageUpdate = useCallback(
		(page: number, pageSize: number) => {
			query.update({
				page: page.toString(),
				pageSize: pageSize.toString(),
			});
		},
		[query]
	);

	const downloadPoliJobsCvAccessReport = useCallback(async () => {
		setDownloading(true);
		setProgress(0);
		try {
			const totalPages = Math.max(
				1,
				Math.ceil((data?.totalCount || 0) / pageSize)
			);
			const allData: PoliJobsCvAccessReportDTO[] = [];

			for (let page = 1; page <= totalPages; page++) {
				const response = await getAllPoliJobsCvAccessReport({
					page: page,
					pageSize: pageSize,
					...query.filters,
				});
				allData.push(...(response.data || []));
				setProgress(Math.round((page / totalPages) * 100));
			}

			await downloadExcel(
				t("files.poliJobsCvAccessSharingReport"),
				excelColumns.map((e) => {
					return {
						header:
							typeof e.title === "string"
								? removeDiacritics(e.title)
								: removeDiacritics(t("applicants." + e.key)),
						key: e.key.toString(),
					};
				}),
				allData
			);

			setDownloadError(false);
		} catch (error) {
			setDownloadError(true);
			openNotification(
				t("reports.errorTexts.downloadFailed"),
				t("reports.errorTexts.downloadFailedMessage"),
				NOTIFICATION_TYPES.ERROR
			);
		} finally {
			setProgress(100);
			setDownloading(false);
		}
	}, [setDownloading, t, columns, query.filters, data?.totalCount, pageSize]);

	return (
		<div className="px-4 flex flex-col gap-4 py-10">
			<Filters
				searchFields={[{ name: "search", label: t("internships.search") }]}
				onUpdate={onUpdate}
				hasSort={true}
				sortOptions={[
					{
						key: PoliJobsReportSortFieldEnum.CvSharesCount,
						label: t("polijobs.sort.cvSharesCount"),
					},
					{
						key: PoliJobsReportSortFieldEnum.CvDownloadsCount,
						label: t("polijobs.sort.cvDownloadsCount"),
					},
				]}
			/>

			<div className="flex flex-col items-end gap-2 mb-4">
				<Button
					onClick={downloadPoliJobsCvAccessReport}
					className="w-full sm:w-auto"
				>
					{t("polijobs.downloadCvAccessSharingReport")}
				</Button>
				{downloading && (
					<div className="w-full sm:w-auto flex justify-end">
						<Progress
							percent={progress}
							status={downloadError ? "exception" : undefined}
							style={{ width: 160 }}
							strokeWidth={6}
						/>
					</div>
				)}
			</div>

			<div className="flex flex-col gap-6">
				<div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
					<Typography.Title
						level={4}
						className="my-0 text-base sm:text-lg md:text-xl"
					>
						{data?.totalCount} {t("filtering.results")}
					</Typography.Title>
					{!loading && (
						<Pagination
							current={Number(query.filters.page) || data?.page || 1}
							pageSize={Number(query.filters.pageSize) || data?.pageSize}
							defaultCurrent={data?.page}
							defaultPageSize={data?.pageSize}
							total={data?.totalCount}
							onChange={onPageUpdate}
							locale={{ items_per_page: t("pagination") }}
						/>
					)}
				</div>

				<div className="overflow-x-auto">
					<Table
						locale={{ emptyText: t("tableText.emptyTable") }}
						columns={columns}
						dataSource={data?.data ?? []}
						pagination={false}
						scroll={{ x: 1000, y: 420 }}
						rowKey={(record) => record.id!}
					/>
				</div>
			</div>
		</div>
	);
};

export default PoliJobsReport;
